import { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
} from '@mui/material'
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Check as CompleteIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material'
import { Client, Task, tasksApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import { useWebSocket } from '../../hooks/useWebSocket'

interface ClientTasksTabProps {
  client: Client
}

const priorityColors: Record<string, 'error' | 'warning' | 'info'> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
}

export default function ClientTasksTab({ client }: ClientTasksTabProps) {
  const { showSuccess, showError } = useNotification()
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; task: Task } | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    dueDate: '',
  })
  const [creating, setCreating] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<Task | null>(null)
  const [deleting, setDeleting] = useState(false)
  const isInitialLoad = useRef(true)

  const loadTasks = useCallback(async () => {
    try {
      if (isInitialLoad.current) setLoading(true)
      setError(null)
      const data = await tasksApi.list({ clientId: client.id, all: true, excludeGateTasks: true })
      setTasks(data ?? [])
    } catch (err) {
      console.error('Error loading tasks:', err)
      setError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
      isInitialLoad.current = false
    }
  }, [client.id])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  useWebSocket({
    task_created: (message) => {
      const task = message.data as Task
      if (task.clientId === client.id) loadTasks()
    },
    task_updated: (message) => {
      const task = message.data as Task
      if (task.clientId === client.id) loadTasks()
    },
    task_completed: (message) => {
      const task = message.data as Task
      if (task.clientId === client.id) loadTasks()
    },
    task_deleted: (message) => {
      const data = message.data as { id: string; clientId?: string }
      if (data.clientId === client.id) loadTasks()
    },
    task_assigned: (message) => {
      const task = message.data as Task
      if (task.clientId === client.id) loadTasks()
    },
  })

  const handleToggleComplete = async (task: Task) => {
    try {
      if (task.status === 'completed') {
        await tasksApi.update({ id: task.id, status: 'pending' })
        showSuccess('Task marked as pending')
      } else {
        await tasksApi.complete(task.id)
        showSuccess('Task completed')
      }
      loadTasks()
    } catch (err) {
      console.error('Error updating task:', err)
      showError(err instanceof Error ? err.message : 'Failed to update task')
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    setMenuAnchor({ el: event.currentTarget, task })
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleStatusChange = async (status: Task['status']) => {
    if (!menuAnchor) return
    try {
      await tasksApi.update({ id: menuAnchor.task.id, status })
      showSuccess(`Task marked as ${status.replace('_', ' ')}`)
      loadTasks()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update task')
    }
    handleMenuClose()
  }

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      showError('Task title is required')
      return
    }

    try {
      setCreating(true)
      await tasksApi.create({
        ...newTask,
        type: 'follow_up',
        clientId: client.id,
        clientName: client.name,
        dueDate: newTask.dueDate || undefined,
        isClientFacing: true,
      })
      showSuccess('Task created successfully')
      setAddDialogOpen(false)
      setNewTask({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
      })
      loadTasks()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create task')
    } finally {
      setCreating(false)
    }
  }

  const handleDeleteTask = async () => {
    if (!deleteConfirm) return
    try {
      setDeleting(true)
      await tasksApi.delete(deleteConfirm.id)
      showSuccess('Task deleted')
      setDeleteConfirm(null)
      loadTasks()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to delete task')
    } finally {
      setDeleting(false)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const isOverdue = (dateStr?: string) => {
    if (!dateStr) return false
    const date = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  // Separate active and completed tasks
  const activeTasks = tasks.filter((t) => t.status !== 'completed')
  const completedTasks = tasks.filter((t) => t.status === 'completed')

  if (loading) {
    return (
      <Box sx={{ minHeight: 200 }}>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={36} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Tasks for {client.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadTasks}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Task
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {tasks.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No tasks for this client yet. Create one to get started.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Active Tasks */}
          {activeTasks.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 100 }}>Due Date</TableCell>
                    <TableCell sx={{ width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeTasks.map((task) => (
                    <TableRow key={task.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={false}
                          onChange={() => handleToggleComplete(task)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {task.title}
                        </Typography>
                        {task.description && (
                          <Typography variant="caption" color="text.secondary">
                            {task.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={task.priority}
                          size="small"
                          color={priorityColors[task.priority] || 'default'}
                          sx={{ fontSize: 11, height: 22 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={isOverdue(task.dueDate) ? 'error' : 'text.primary'}
                          fontWeight={isOverdue(task.dueDate) ? 600 : 400}
                        >
                          {formatDate(task.dueDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Completed Tasks */}
          {completedTasks.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Completed ({completedTasks.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {completedTasks.map((task) => (
                      <TableRow key={task.id} sx={{ opacity: 0.6 }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked
                            onChange={() => handleToggleComplete(task)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ textDecoration: 'line-through' }}
                          >
                            {task.title}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 100 }}>
                          <Typography variant="body2">{formatDate(task.dueDate)}</Typography>
                        </TableCell>
                        <TableCell sx={{ width: 50 }}>
                          <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)}>
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor?.el}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuAnchor?.task.status !== 'in_progress' && (
          <MenuItem onClick={() => handleStatusChange('in_progress')}>
            <ListItemIcon>
              <StartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Start</ListItemText>
          </MenuItem>
        )}
        {menuAnchor?.task.status !== 'completed' && (
          <MenuItem onClick={() => handleStatusChange('completed')}>
            <ListItemIcon>
              <CompleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Complete</ListItemText>
          </MenuItem>
        )}
        {menuAnchor?.task.status === 'completed' && (
          <MenuItem onClick={() => handleStatusChange('pending')}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reopen</ListItemText>
          </MenuItem>
        )}
        {!menuAnchor?.task.isGateTask && (
          <MenuItem
            onClick={() => {
              setDeleteConfirm(menuAnchor?.task || null)
              handleMenuClose()
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Add Task Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Task for {client.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Task Title"
              fullWidth
              required
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  label="Priority"
                  onChange={(e) =>
                    setNewTask({ ...newTask, priority: e.target.value as Task['priority'] })
                  }
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Due Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={newTask.dueDate}
              onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreateTask} variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={20} /> : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={Boolean(deleteConfirm)} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently delete &quot;{deleteConfirm?.title}&quot;. It cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteTask}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
